// In-memory access token with silent refresh via httpOnly cookie
let accessToken: string | null = null;
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function attemptRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  isRefreshing = true;
  refreshPromise = fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  })
    .then(async (resp) => {
      if (!resp.ok) return false;
      const data = await resp.json();
      if (data?.success && data?.token) {
        accessToken = data.token;
        return true;
      }
      return false;
    })
    .catch(() => false)
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  return refreshPromise;
}

// API utility with automatic silent refresh on 401
export const apiRequest = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Don't set Content-Type for FormData - browser will set it with boundary
  const isFormData = options.body instanceof FormData;

  const config: RequestInit = {
    ...options,
    credentials: 'include',
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    },
  };



  const envBase = (typeof process !== 'undefined' && (process as any).env && (process as any).env.REACT_APP_API_BASE) || '';
  const base = envBase.replace(/\/$/, '');
  const fullUrl = url.startsWith('http') ? url : (base ? `${base}${url}` : url);

  let response = await fetch(fullUrl, config);

  if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
    const refreshed = await attemptRefresh();
    if (refreshed && accessToken) {
      const retryConfig: RequestInit = {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      };
      response = await fetch(fullUrl, retryConfig);
      if (response.status !== 401) return response;
    }
    // Refresh failed or still unauthorized → clear token but don't reload for auth endpoints
    accessToken = null;
  }

  return response;
};

// Upload with progress tracking using XMLHttpRequest
export const uploadWithProgress = async (
  url: string,
  formData: FormData,
  onProgress: (percent: number) => void
): Promise<{ success: boolean; data?: any; error?: string }> => {
  // Try to refresh token first if we don't have one
  if (!accessToken) {
    await attemptRefresh();
  }

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    });

    // Handle completion
    xhr.addEventListener('load', async () => {
      // Handle 401 - try to refresh token and retry
      if (xhr.status === 401) {
        const refreshed = await attemptRefresh();
        if (refreshed && accessToken) {
          // Retry upload with new token
          const retryResult = await uploadWithProgress(url, formData, onProgress);
          resolve(retryResult);
          return;
        } else {
          resolve({ success: false, error: 'Authentication failed. Please login again.' });
          return;
        }
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ success: true, data });
        } catch (e) {
          resolve({ success: true });
        }
      } else {
        try {
          const errorData = JSON.parse(xhr.responseText);
          resolve({ success: false, error: errorData.error || 'Upload failed' });
        } catch (e) {
          resolve({ success: false, error: `Upload failed with status ${xhr.status}` });
        }
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      resolve({ success: false, error: 'Network error' });
    });

    xhr.addEventListener('abort', () => {
      resolve({ success: false, error: 'Upload cancelled' });
    });

    // Open connection and send with auth
    const envBase = (typeof process !== 'undefined' && (process as any).env && (process as any).env.REACT_APP_API_BASE) || '';
    const base = envBase.replace(/\/$/, '');
    const fullUrl = url.startsWith('http') ? url : (base ? `${base}${url}` : url);

    xhr.open('POST', fullUrl);
    xhr.withCredentials = true; // Include cookies for refresh token

    if (accessToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    }

    xhr.send(formData);
  });
};

export default apiRequest;